import React, { useEffect, useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, TextField, Chip, Box, Link, Button
} from '@mui/material';
import axios from 'axios';
import * as constants from "../constants.js"
import { useNavigate, Link as RouterLink } from "react-router-dom";
import ResponsiveAppBar from "../ResponsiveAppBar.js";
import ResponsiveFooter from "../ResponsiveFooter.js";

// Every row carries its user and region, but repeating them down the table
// makes it very hard to read. Since the backend returns the rows already
// grouped (user, then region, then scenario), a cell is blanked whenever it
// repeats the row above it -- the usual spreadsheet convention.
const repeated = (rows, index, key) =>
  index > 0 && rows[index - 1][key] === rows[index][key];

// Status values actually present in the database are 'done', 'created' and
// 'computing'; 'queued' and 'error' are set transiently by the queue, so they
// are covered too.
const statusColor = (status) => {
  if (status === 'done') return 'success';
  if (status === 'computing') return 'info';
  if (status === 'queued') return 'warning';
  if (status === 'error' || status === 'failed') return 'error';
  return 'default';
};

const AdminOverview = () => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ users: 0, regions: 0, scenarios: 0 });
  const [filter, setFilter] = useState('');
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.create({ withCredentials: true })
          .get("//" + constants.BASE_URL + "/getAdminOverview");
        setRows(resp.data.rows);
        setTotals(resp.data.totals);
        setLoaded(true);
      } catch (error) {
        // The route is admin-only and answers 401 for everyone else; there is
        // nothing useful to show a non-admin, so send them home.
        console.error("Error fetching admin overview:", error);
        navigate('/');
      }
    })();
  }, [navigate]);

  const visibleRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.user_name, r.user_email, r.region_name, r.region_federal_state, r.scenario_name, r.scenario_status]
        .some((v) => (v || '').toString().toLowerCase().includes(needle))
    );
  }, [rows, filter]);

  // For the rare computation that gets stuck: marks the scenario as done and
  // removes it from the queue. It does not produce any results.
  const resetStatus = async (scenarioId, scenarioName) => {
    if (!window.confirm(`Status von Szenario "${scenarioName}" auf "done" setzen? `
      + 'Die Berechnung wird dabei aus der Queue entfernt.')) return;
    try {
      await axios.create({ withCredentials: true })
        .post("//" + constants.BASE_URL + "/resetScenarioStatus", { id: scenarioId });
      setRows((prev) => prev.map((r) =>
        r.scenario_id === scenarioId ? { ...r, scenario_status: 'done' } : r));
    } catch (error) {
      alert("Fehler beim Zurücksetzen des Status: " + error);
    }
  };

  const cell = (value) => (value === null || value === undefined || value === '' ? '—' : value);

  return (
    <div className="home">
      <ResponsiveAppBar />
      <div className="body-content">
        <br />
        <h2>Gesamtübersicht</h2>
        <p>Alle Benutzer, Regionen und Szenarien im System.</p>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`${totals.users} Benutzer`} />
          <Chip label={`${totals.regions} Regionen`} />
          <Chip label={`${totals.scenarios} Szenarien`} />
          <TextField
            size="small"
            label="Filtern"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ minWidth: 260, ml: 'auto' }}
          />
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: '65vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell><b>Benutzer</b></TableCell>
                <TableCell><b>E-Mail</b></TableCell>
                <TableCell><b>Admin</b></TableCell>
                <TableCell><b>Region</b></TableCell>
                <TableCell align="right"><b>Fläche (km²)</b></TableCell>
                <TableCell><b>Bundesland</b></TableCell>
                <TableCell><b>Szenario</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell align="right"><b>Regen (mm)</b></TableCell>
                <TableCell align="right"><b>Dauer (h)</b></TableCell>
                <TableCell><b>Methode</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, i) => {
                // Filtering breaks the original adjacency, so only collapse
                // repeats when the list is unfiltered.
                const collapse = !filter.trim();
                const sameUser = collapse && repeated(visibleRows, i, 'user_id');
                const sameRegion = collapse && sameUser && repeated(visibleRows, i, 'region_id');
                return (
                  <TableRow key={`${row.user_id}-${row.region_id}-${row.scenario_id}-${i}`} hover>
                    <TableCell>{sameUser ? '' : cell(row.user_name)}</TableCell>
                    <TableCell>{sameUser ? '' : cell(row.user_email)}</TableCell>
                    <TableCell>{sameUser ? '' : (row.user_admin ? 'ja' : '')}</TableCell>
                    <TableCell>
                      {sameRegion ? '' : (row.region_id
                        // Admins may open any region, to debug customer problems.
                        ? <Link component={RouterLink} to={`/region-overview/${row.region_id}`}>
                            {cell(row.region_name)}
                          </Link>
                        : cell(row.region_name))}
                    </TableCell>
                    <TableCell align="right">
                      {sameRegion || row.region_area === null ? '' : Number(row.region_area).toFixed(2)}
                    </TableCell>
                    <TableCell>{sameRegion ? '' : cell(row.region_federal_state)}</TableCell>
                    <TableCell>{cell(row.scenario_name)}</TableCell>
                    <TableCell>
                      {row.scenario_status
                        ? <Chip size="small" label={row.scenario_status} color={statusColor(row.scenario_status)} />
                        : '—'}
                      {row.scenario_id && row.scenario_status !== 'done' && (
                        <Button size="small" color="warning" sx={{ ml: 1 }}
                          onClick={() => resetStatus(row.scenario_id, row.scenario_name)}>
                          Auf done setzen
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="right">{cell(row.rain_amount)}</TableCell>
                    <TableCell align="right">
                      {row.rain_duration === null ? '—' : (row.rain_duration / 3600).toFixed(1)}
                    </TableCell>
                    <TableCell>{cell(row.computation_method)}</TableCell>
                  </TableRow>
                );
              })}
              {loaded && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>Keine Einträge gefunden.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loaded && <Typography sx={{ mt: 2 }}>Lade…</Typography>}
        <br /><br />
      </div>
      <ResponsiveFooter />
    </div>
  );
};

export default AdminOverview;
